import { useMemo, useState } from "react"

export default function OrganizationTable({
  organizations,
  selectedIds,
  onToggleSelect,
  onManage,
}) {
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" })

  const handleSortChange = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" }
      }
      return { key, direction: "asc" }
    })
  }

  const sortedOrganizations = useMemo(() => {
    if (!Array.isArray(organizations)) return []

    const sorted = [...organizations].sort((a, b) => {
      const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1

      if (sortConfig.key === "name") {
        const aName = String(a.name || "").toLowerCase()
        const bName = String(b.name || "").toLowerCase()
        if (aName < bName) return -1 * directionMultiplier
        if (aName > bName) return 1 * directionMultiplier
        return 0
      }

      const aValue = Number(a[sortConfig.key] ?? 0)
      const bValue = Number(b[sortConfig.key] ?? 0)
      return (aValue - bValue) * directionMultiplier
    })

    return sorted
  }, [organizations, sortConfig.direction, sortConfig.key])

  const getAriaSort = (key) => {
    if (sortConfig.key !== key) return "none"
    return sortConfig.direction === "asc" ? "ascending" : "descending"
  }

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ""
    return sortConfig.direction === "asc" ? " ▲" : " ▼"
  }

  if (!organizations?.length) {
    return null
  }

  return (
    <div className="organization-table table-scroll">
      <table className="ui-table">
        <thead>
          <tr>
            <th scope="col" className="organization-table__checkbox-column">
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" aria-sort={getAriaSort("name")}>
              <button type="button" className="organization-table__sort-button" onClick={() => handleSortChange("name")}>
                {`Name${getSortIndicator("name")}`}
              </button>
            </th>
            <th scope="col" aria-sort={getAriaSort("member_count")}>
              <button
                type="button"
                className="organization-table__sort-button"
                onClick={() => handleSortChange("member_count")}
              >
                {`Members${getSortIndicator("member_count")}`}
              </button>
            </th>
            <th scope="col" aria-sort={getAriaSort("website_count")}>
              <button
                type="button"
                className="organization-table__sort-button"
                onClick={() => handleSortChange("website_count")}
              >
                {`Websites${getSortIndicator("website_count")}`}
              </button>
            </th>
            <th scope="col" className="organization-table__actions-column">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedOrganizations.map((org) => {
            const isSelected = selectedIds.includes(org.id)
            return (
              <tr key={org.id} className={isSelected ? "organization-table__row--selected" : undefined}>
                <td className="organization-table__checkbox-cell">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(org.id)}
                    aria-label={`Select ${org.name}`}
                  />
                </td>
                <td>{org.name}</td>
                <td>{org.member_count}</td>
                <td>{org.website_count}</td>
                <td className="organization-table__actions-cell">
                  <button
                    type="button"
                    className="button--secondary"
                    onClick={() => onManage(org.id)}
                  >
                    Manage
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
